import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MacroCard } from "@/components/MacroCard";
import { ImageCropModal } from "@/components/ImageCropModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, X, Pen, UserPlus, UserMinus } from "lucide-react";
import verifiedBadge from "@/assets/verified-badge.png";
import { useAuth } from "@/hooks/useAuth";
import { useAuthModal } from "@/hooks/useAuthModal";
import { formatFollowerCount } from "@/lib/utils";

const Profile = () => {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [profile, setProfile] = useState<any>(null);
  const [macros, setMacros] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalInstalls, setTotalInstalls] = useState(0);

  // Follow states
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [newBio, setNewBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  useEffect(() => {
    if (username) {
      fetchProfile();
    }
  }, [username]);
  const fetchProfile = async () => {
    try {
      setLoading(true);

      // Fetch profile (excluding email for security)
      const {
        data: profileData,
        error: profileError
      } = await supabase.from('public_profiles').select('id, username, full_name, avatar_url, bio, verified, follower_count_override, created_at, updated_at').eq('username', username).single();
      if (profileError) throw profileError;
      setProfile(profileData);

      // Fetch user's published macros with tags
      const {
        data: macrosData,
        error: macrosError
      } = await supabase.from('macros').select(`
          *,
          macro_tags (
            tags (name)
          )
        `).eq('owner_id', profileData.id).eq('is_published', true).eq('is_approved', true).order('created_at', {
        ascending: false
      });
      if (macrosError) throw macrosError;
      setMacros(macrosData || []);

      // Calculate total installs
      const total = (macrosData || []).reduce((sum, m) => sum + (m.installs_count || 0), 0);
      setTotalInstalls(total);

      // Use follower_count_override if set, otherwise fetch real count
      if (profileData.follower_count_override !== null) {
        setFollowerCount(profileData.follower_count_override);
      } else {
        const { count: followers } = await supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('following_id', profileData.id);
        setFollowerCount(followers || 0);
      }

      // Check if current user is following this profile
      if (currentUser) {
        const { data: followData } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_id', currentUser.id)
          .eq('following_id', profileData.id)
          .maybeSingle();
        setIsFollowing(!!followData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleCroppedImage = async (croppedFile: File) => {
    try {
      const fileName = `${profile.id}/${Date.now()}.png`;
      const {
        error: uploadError
      } = await supabase.storage.from('avatars').upload(fileName, croppedFile, {
        upsert: true
      });
      if (uploadError) throw uploadError;
      const {
        data: {
          publicUrl
        }
      } = supabase.storage.from('avatars').getPublicUrl(fileName);
      const {
        error: updateError
      } = await supabase.from('profiles').update({
        avatar_url: publicUrl
      }).eq('id', profile.id);
      if (updateError) throw updateError;
      toast.success("Profile picture updated!");
      fetchProfile();
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error("Failed to update profile picture");
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUser.id)
          .eq('following_id', profile.id);
        if (error) throw error;
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        toast.success(`Unfollowed ${profile.full_name || profile.username}`);
      } else {
        // Follow
        const { error } = await supabase
          .from('follows')
          .insert({ follower_id: currentUser.id, following_id: profile.id });
        if (error) throw error;
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        toast.success(`Following ${profile.full_name || profile.username}`);
      }
    } catch (error: any) {
      console.error('Follow error:', error);
      toast.error(error.message || "Failed to update follow status");
    } finally {
      setFollowLoading(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>;
  }
  if (!profile) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Profile not found</h1>
          <p className="text-muted-foreground mb-6">
            The user you're looking for doesn't exist.
          </p>
          <Link to="/vault">
            <Button>Browse Macros</Button>
          </Link>
        </div>
      </div>;
  }
  const isOwner = currentUser && profile && currentUser.id === profile.id;
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>{profile.full_name || profile.username} - MacroVault</title>
        <meta name="description" content={`View ${profile.full_name || profile.username}'s profile and their published VBA macros on MacroVault.`} />
        <link rel="canonical" href={`https://findmacros.com/profile/${profile.username}`} />
      </Helmet>
      <Header />
      
      <div className="container mx-auto px-4 pt-24 sm:pt-28 lg:pt-32 pb-12 max-w-5xl">
        {/* Profile Header */}
        <Card className="mb-8 bg-muted">
          <CardContent className="pt-12 pb-8 relative bg-muted">
            {/* Edit/Done Button (only for profile owner) */}
             {isOwner && <Button onClick={() => {
            if (!isEditMode) {
              // Initialize fields with current values when entering edit mode
              setNewDisplayName(profile.full_name || "");
              setNewCompany(profile.company || "");
              setNewBio(profile.bio || "");
            } else {
              // Reset fields when exiting edit mode
              setNewDisplayName("");
              setNewCompany("");
              setNewBio("");
            }
            setIsEditMode(!isEditMode);
          }} size="sm" variant="ghost" className="absolute top-4 right-4 hover:bg-transparent group">
                <Pen className={`h-4 w-4 transition-all ${isEditMode ? 'fill-black dark:fill-white' : 'group-hover:fill-black dark:group-hover:fill-white'}`} strokeWidth={isEditMode ? 0 : 2} />
              </Button>}
            
            <div className="flex flex-col items-center text-center">
              {/* Avatar */}
              <div className="mb-6 relative group">
                {profile.avatar_url ? <img src={profile.avatar_url} alt={profile.full_name || profile.username} className="w-24 h-24 rounded-full object-cover" /> : <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-2xl font-bold text-primary-foreground">
                    {getInitials(profile.full_name || profile.username)}
                  </div>}
                {isEditMode && <label htmlFor="avatar-upload" className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                    <Upload className="h-6 w-6 text-white" />
                    <input id="avatar-upload" type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                  </label>}
              </div>

              {/* Edit Mode */}
              {isEditMode ? <div className="w-full max-w-2xl space-y-6 mb-6">
                  <div className="text-center space-y-2 mb-8">
                    <h2 className="text-3xl font-bold">Edit your profile details</h2>
                    <p className="text-muted-foreground">Show people who you are and why they should trust your Macros!</p>
                  </div>

              <form onSubmit={async e => {
                e.preventDefault();
                setSaving(true);
                try {
                  const updates: any = {};
                  
                  // Check if display name changed (allow clearing)
                  const trimmedDisplayName = newDisplayName.trim();
                  if (trimmedDisplayName !== (profile.full_name || "")) {
                    updates.full_name = trimmedDisplayName || null;
                  }
                  
                  // Check if company changed (allow clearing)
                  const trimmedCompany = newCompany.trim();
                  if (trimmedCompany !== (profile.company || "")) {
                    updates.company = trimmedCompany || null;
                  }
                  
                  // Check if bio changed (allow clearing)
                  const trimmedBio = newBio.trim();
                  if (trimmedBio !== (profile.bio || "")) {
                    updates.bio = trimmedBio || null;
                  }
                  if (Object.keys(updates).length > 0) {
                    const {
                      error
                    } = await supabase.from("profiles").update(updates).eq("id", currentUser.id);
                    if (error) throw error;
                    toast.success("Profile updated successfully");
                    await fetchProfile();
                  } else {
                    toast.info("No changes to save");
                  }
                  setNewDisplayName("");
                  setNewCompany("");
                  setNewBio("");
                  setIsEditMode(false);
                } catch (error: any) {
                  console.error("Profile update error:", error);
                  toast.error(error.message || "Failed to update profile");
                } finally {
                  setSaving(false);
                }
              }} className="space-y-6">
                  <div className="space-y-2">
                      <Label htmlFor="displayName" className="text-sm font-medium text-left block">
                        Display Name
                      </Label>
                      <Input id="displayName" value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder="E.g. John Doe" className="bg-background" />
                    </div>

                  <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm font-medium text-left block">
                        Company
                      </Label>
                      <Input id="company" value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="E.g. Acme Corp" className="bg-background" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio" className="text-sm font-medium text-left block">
                        Bio
                      </Label>
                      <Textarea id="bio" value={newBio} onChange={e => setNewBio(e.target.value)} placeholder="Tell others about yourself..." className="min-h-[100px] bg-background resize-none" maxLength={280} />
                      <p className="text-xs text-muted-foreground text-right">
                        {newBio.length}/280 characters
                      </p>
                    </div>

                    <div className="flex gap-3 justify-end pt-4">
                      <Button type="button" variant="ghost" onClick={() => {
                    setIsEditMode(false);
                    setNewDisplayName("");
                    setNewCompany("");
                    setNewBio("");
                  }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </div> : <>
                  {/* Name and Username - View Mode */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 justify-center mb-1">
                      <h1 className="text-3xl font-bold">
                        {profile.full_name || profile.username}
                      </h1>
                      {profile.verified && <img src={verifiedBadge} alt="Verified" className="h-6 w-6" />}
                    </div>
                    {profile.company && <p className="text-muted-foreground text-sm">{profile.company}</p>}
                  </div>

                  {/* Bio - View Mode */}
                  {profile.bio && <p className="text-muted-foreground max-w-2xl mb-6">
                      {profile.bio}
                    </p>}
                </>}

              {/* Encourage profile completion */}
              {isOwner && (!profile.bio || !profile.company || !profile.avatar_url)}

              {/* Follow Button (only for non-owners) */}
              {!isOwner && (
                <Button
                  onClick={handleFollowToggle}
                  disabled={followLoading}
                  variant={isFollowing ? "outline" : "default"}
                  className="mb-6"
                >
                  {isFollowing ? (
                    <>
                      <UserMinus className="h-4 w-4 mr-2" />
                      Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Follow
                    </>
                  )}
                </Button>
              )}

              {/* Stats */}
              <div className="flex gap-8 bg-muted p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold">{formatFollowerCount(followerCount)}</div>
                  <div className="text-sm text-muted-foreground">Followers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{macros.length}</div>
                  <div className="text-sm text-muted-foreground">Macros</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalInstalls}</div>
                  <div className="text-sm text-muted-foreground">Total Installs</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Crop Modal */}
        <ImageCropModal open={showCropModal} imageSrc={selectedImage || ""} onCropComplete={handleCroppedImage} onClose={() => {
        setShowCropModal(false);
        setSelectedImage(null);
      }} cropShape="round" />

        {/* Macros Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold">
            Macros by {profile.full_name || profile.username}
          </h2>
        </div>

        {macros.length === 0 ? <Card className="bg-muted">
            <CardContent className="py-12 text-center bg-muted">
              <p className="text-muted-foreground">
                {isOwner ? "You haven't published any macros yet." : "This user hasn't published any macros yet."}
              </p>
              {isOwner && <Link to="/upload">
                  <Button className="mt-4">Submit Your First Macro</Button>
                </Link>}
            </CardContent>
          </Card> : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {macros.map(macro => <MacroCard key={macro.id} id={macro.id} slug={macro.slug} name={macro.name} shortDesc={macro.short_desc || ''} ownerHandle={profile.username} ownerDisplayName={profile.full_name || profile.username} ownerVerified={profile.verified} riskScore={macro.risk_score} tags={macro.macro_tags?.map((mt: any) => mt.tags.name) || []} viewsCount={macro.views_count || 0} screenshotUrl={macro.screenshot_url} />)}
          </div>}
      </div>
    </div>;
};
export default Profile;